// Centralized API endpoints
// Group endpoints by domain for consistency and single source of truth

export const AuthEndpoints = {
    manualLogin: '/v1/customers/manual-login',
    signIn: '/v1/customers/sign-in',
    forgotOtp: '/v1/customers/forgot-otp',
    verifyForgotOtp: '/v1/customers/verify-forgot-otp',
    resetPassword: '/v1/customers/reset-password', // For forgot password flow after OTP verification
    setForgotPassword: '/v1/customers/set-forgot-password', // For forgot password flow after OTP verification
    uaePassCallback: '/v1/uae-pass/callback',
    /** ADHA flow only — existing UAE Pass callback flow is unaffected. */
    uaePassLogin: '/v1/uae-pass/login',
} as const;

export const ProfileEndpoints = {
    profileDetails: '/v1/customers/profile-details',
    unitInstallments: '/v1/customers/unit-installments',
    setPasscode: '/v1/customers/set-passcode',
    verifyPasscode: '/v1/customers/verify-passcode',
    changePasscode: '/v1/customers/change-passcode',
    changePassword: '/v1/customers/change-password',
    skipPasscode: '/v1/customers/skip-passcode',
    forgotPasscode: '/v1/customers/forgot-passcode',
    documentDownload: '/v1/document/download',
    tnc: '/v1/customers/tnc',
    deleteUser: '/v1/customers/delete-user',
    /** POST — Pay Now top-up method (bank transfer / mortgage). */
    updateTopUpType: '/v1/customers/updateTopUpType',
} as const;

export const PaymentEndpoints = {
    fees: '/v1/payment/fees',
    createCheckout: '/v1/payment/create-checkout',
    status: '/v1/payment/status',
} as const;

export const RegistrationEndpoints = {
    register: '/v1/customers/register',
    customerDetails: '/v1/customers/details',
    sendOtp: '/v1/customers/send-otp',
    verifyOtp: '/v1/customers/verify-otp',
    verifyOtpRegistration: '/v1/customers/verify-otp-registration',
    verifyDetails: '/v1/customers/verify-details',
    registrationStatus: '/v1/customers/registration-status',
} as const;

export const SupportEndpoints = {
    serviceRequests: '/v1/customers/service-requests',
    serviceRequestCategories: '/v1/customers/get-list-values',
    createServiceRequest: '/v1/customers/service-request',
} as const;

/**
 * SHARED API — Properties / units endpoints (web + native).
 * Access: `Endpoints.property` from `@/core/api/endpoints`.
 *
 * LISTING:  GET units          → AvailableUnitsResponse   (`@/core/api/models/properties`)
 *           query: { max_price?, page?=0, limit?=20 } (no project_id — Bearer only)
 * DETAIL:   GET unitDetails    → UnitDetailsResponse
 *           path: /v1/customers/profile  (Bearer only — no ID query)
 * PROJECTS: GET projects       → GuestProjectsResponse (public catalogue, Basic Auth)
 *           query: { page?=0, limit?=200 }; response: page, limit, hasNext, details[]
 *           details item may include latitude / longitude for map pin
 *
 * Units/detail auth: Bearer JWT from POST /v1/uae-pass/login (`apiClient` injects `auth.token`).
 * Projects auth: ADHA Basic Auth via `getAdhaBasicAuthHeaders()` (preserved by apiClient).
 * Prefer `unitsService` / `guestProjectsService`.
 */
export const PropertyEndpoints = {
    /** GET /v1/units */
    units: '/v1/units',
    /** GET /v1/customers/profile (ADHA project / profile after login, Bearer only) */
    unitDetails: '/v1/customers/profile',
    /** GET /v1/projects — available projects + unit types (Basic Auth) */
    projects: '/v1/projects',
} as const;

/**
 * SHARED API — Appointments (web + native).
 * Access: `Endpoints.appointment` from `@/core/api/endpoints`.
 *
 * SLOTS:     GET  /v1/appointments/slots
 * UPCOMING:  GET  /v1/appointments/upcoming
 * SCHEDULE:  POST /v1/appointments/schedule  (Bearer only — no customer_id)
 * UPDATE:    PATCH /v1/appointments/update  (cancel + reschedule; Bearer only — no customer_id)
 *
 * Auth: Bearer JWT from login (`apiClient` injects `auth.token`).
 * Prefer `appointmentService` from `@/core/api/services/appointmentService`.
 * Responses are dummy until Boomi APIs land.
 */
export const AppointmentEndpoints = {
    /** GET /v1/appointments/slots */
    slots: '/v1/appointments/slots',
    /** GET /v1/appointments/upcoming */
    upcoming: '/v1/appointments/upcoming',
    /** POST /v1/appointments/schedule */
    schedule: '/v1/appointments/schedule',
    /** PATCH /v1/appointments/update */
    update: '/v1/appointments/update',
} as const;

/**
 * ADHA web reservation APIs. The customer id is used as the Salesforce lead id.
 * Auth: UAE Pass Bearer token injected by `apiClient`.
 */
export const BookingEndpoints = {
    /**
     * GET /v1/bookings
     * Customer comes from the bearer token — no lead id in the path.
     * No booking → HTTP 200:
     * `{ status: "success", message: "Booking not found.", details: null }`
     */
    details: '/v1/bookings',
    /** POST /v1/bookings — create (`unit_id` set) / cancel (`unit_id` empty). Customer is the bearer token. */
    create: '/v1/bookings',
    /** POST /v1/bookings/resend-form */
    resendForm: '/v1/bookings/resend-form',
} as const;

export type EndpointMap = {
    auth: typeof AuthEndpoints;
    profile: typeof ProfileEndpoints;
    payment: typeof PaymentEndpoints;
    registration: typeof RegistrationEndpoints;
    support: typeof SupportEndpoints;
    property: typeof PropertyEndpoints;
    appointment: typeof AppointmentEndpoints;
    booking: typeof BookingEndpoints;
};

export const Endpoints: EndpointMap = {
    auth: AuthEndpoints,
    profile: ProfileEndpoints,
    payment: PaymentEndpoints,
    registration: RegistrationEndpoints,
    support: SupportEndpoints,
    property: PropertyEndpoints,
    appointment: AppointmentEndpoints,
    booking: BookingEndpoints,
};
